import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete,
  TextField, Chip, Box, Typography, CircularProgress, Alert,
} from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { updatePitch } from './services/pitchService';

// Anställda kan vara medredaktörer – inbjudna externa konsulter har inte
// åtkomst till pitchar (isEmployee i firestore.rules).
const EMPLOYEE_DOMAINS = ['tikab.com', 'comdeva.se'];
const isEmployeeEmail = email =>
  EMPLOYEE_DOMAINS.includes((email || '').toLowerCase().split('@')[1]);
const nameOf = u => u.displayName || u.name || u.email || u.id;

/**
 * Ägaren lägger till och tar bort medredaktörer (pitches.collaborators).
 * Medredaktörer får redigera pitchen men inte ändra listan eller radera den.
 */
export default function CollaboratorsDialog({ open, onClose, pitchId, ownerId, collaborators, onSaved }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setLoading(true);
    getDocs(collection(db, 'users'))
      .then(snap => {
        const all = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => u.id !== ownerId && isEmployeeEmail(u.email))
          .sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'sv'));
        setUsers(all);
        setSelected(all.filter(u => (collaborators || []).includes(u.id)));
      })
      .catch(() => setError('Kunde inte läsa medarbetarna.'))
      .finally(() => setLoading(false));
  }, [open, ownerId, collaborators]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const ids = selected.map(u => u.id);
      await updatePitch(pitchId, { collaborators: ids });
      onSaved(ids);
      onClose();
    } catch {
      setError('Kunde inte spara medredaktörerna.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Medredaktörer</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Medredaktörer kan redigera och spara pitchen. Bara du som äger den kan ändra
          listan och radera pitchen. Alla anställda kan se pitchen.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Autocomplete
            multiple
            options={users}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={nameOf}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderTags={(value, getTagProps) => value.map((u, i) => (
              <Chip {...getTagProps({ index: i })} key={u.id} label={nameOf(u)} size="small" />
            ))}
            renderInput={params => (
              <TextField {...params} label="Lägg till medarbetare" placeholder="Sök namn" />
            )}
            noOptionsText="Inga medarbetare hittades"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Sparar…' : 'Spara'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
